import React from 'react';
import clsx from 'clsx';

export interface MenuItem {
  label: React.ReactNode;
  onSelect: () => void;
  icon?: React.ReactNode;
  kbd?: string;
  danger?: boolean;
  disabled?: boolean;
}

export interface MenuProps {
  /** Items and dividers (literal `'sep'`) in render order. */
  items: Array<MenuItem | 'sep'>;
  /** Accessible label for the menu. Required. */
  'aria-label': string;
  /** Called when user presses Esc or clicks outside. */
  onClose?: () => void;
  className?: string;
}

export const Menu: React.FC<MenuProps> = ({
  items,
  'aria-label': ariaLabel,
  onClose,
  className,
}) => {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = React.useState<number>(() => {
    const idx = items.findIndex(
      (i) => i !== 'sep' && !(i as MenuItem).disabled,
    );
    return idx === -1 ? 0 : idx;
  });

  const itemIndexes = items
    .map((item, idx) => ({ item, idx }))
    .filter((entry) => entry.item !== 'sep' && !(entry.item as MenuItem).disabled)
    .map((entry) => entry.idx);

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const buttons = root.querySelectorAll<HTMLButtonElement>(
      'button[role="menuitem"]:not([disabled])',
    );
    const rawIdx = itemIndexes.indexOf(activeIndex);
    if (rawIdx >= 0 && buttons[rawIdx]) {
      buttons[rawIdx].focus();
    }
  }, [activeIndex, itemIndexes]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose?.();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentPos = itemIndexes.indexOf(activeIndex);
    let nextPos = currentPos;
    if (event.key === 'ArrowDown') {
      nextPos = currentPos < itemIndexes.length - 1 ? currentPos + 1 : 0;
    } else if (event.key === 'ArrowUp') {
      nextPos = currentPos > 0 ? currentPos - 1 : itemIndexes.length - 1;
    } else if (event.key === 'Home') {
      nextPos = 0;
    } else if (event.key === 'End') {
      nextPos = itemIndexes.length - 1;
    }
    setActiveIndex(itemIndexes[nextPos]);
  };

  return (
    <div
      ref={rootRef}
      role="menu"
      aria-label={ariaLabel}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className={clsx(
        'min-w-[200px] bg-surface-2 border border-white/[0.08] rounded-ds-lg p-1 shadow-ds-e3',
        className,
      )}
    >
      {items.map((item, idx) => {
        if (item === 'sep') {
          return (
            <div
              key={`sep-${idx}`}
              role="separator"
              className="h-px bg-white/[0.08] mx-1 my-1"
            />
          );
        }
        return (
          <button
            key={idx}
            role="menuitem"
            type="button"
            tabIndex={-1}
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              item.onSelect();
            }}
            className={clsx(
              'w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-ds-md text-[13px]',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              item.danger
                ? 'text-danger-text hover:bg-danger-tint'
                : 'text-slate-300 hover:bg-white/[0.06] hover:text-slate-50',
            )}
          >
            {item.icon && <span className="shrink-0">{item.icon}</span>}
            <span className="flex-1">{item.label}</span>
            {item.kbd && (
              <span className="font-mono text-[10px] text-slate-500">
                {item.kbd}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Menu;
